import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import formatDateWithTimezone from "../TimezoneDateFormatter/TimezoneDateFormatter";

/**
 * SuperadminDateFormatter Component
 * Formats dates in New York timezone by default for superadmin section
 * 
 * This component always uses:
 * - Timezone: America/New_York
 * - Date Format: MM/DD/YYYY
 * - Time Format: 12h
 * 
 * Usage:
 * <SuperadminDateFormatter date={dateString} />
 * 
 * Props:
 * @param {string|Date} date - The date to format (required)
 * @param {string} dateFormat - Optional custom date format (defaults to "MM/DD/YYYY")
 * @param {string} timeFormat - Optional custom time format "12h" or "24h" (defaults to "12h")
 * @param {boolean} includeSeconds - Whether to include seconds (default: true)
 * @param {string} className - Optional CSS class name
 * @param {object} style - Optional inline styles
 */
const SuperadminDateFormatter = ({
  date,
  dateFormat: customDateFormat = "MM/DD/YYYY",
  timeFormat: customTimeFormat = "12h",
  includeSeconds = true,
  className = "",
  style = {},
}) => {
  // Superadmin always uses New York timezone
  const timezone = "America/New_York";
  const dateFormat = customDateFormat || "MM/DD/YYYY";
  const timeFormat = customTimeFormat || "12h";

  // Format the date
  const formattedDate = formatDateWithTimezone(
    date,
    timezone,
    dateFormat,
    timeFormat,
    includeSeconds
  );

  return (
    <span className={className} style={style}>
      {formattedDate}
    </span>
  );
};

/**
 * Hook version for programmatic use in superadmin section
 * Returns a function that formats dates using New York timezone
 * 
 * Usage:
 * const formatDate = useSuperadminDateFormatter();
 * const formatted = formatDate(dateString);
 */
export const useSuperadminDateFormatter = () => {
  return (date, options = {}) => {
    const {
      dateFormat: customDateFormat = "MM/DD/YYYY",
      timeFormat: customTimeFormat = "12h",
      includeSeconds = true,
    } = options;

    if (!date) {
      return "-";
    }

    // Superadmin always uses New York timezone
    const timezone = "America/New_York";
    const dateFormat = customDateFormat || "MM/DD/YYYY";
    const timeFormat = customTimeFormat || "12h";

    return formatDateWithTimezone(
      date,
      timezone,
      dateFormat,
      timeFormat,
      includeSeconds
    );
  };
};

export default SuperadminDateFormatter;
